import CardHeader from "components/CardHeader";
import DevicesAndSessions from "components/DevicesAndSessions";
import PageMeta from "components/PageMeta";
import { GetRouterQuery } from "components/Utils/GetRouterQuery";
import { Account } from "models/Account";
import { Person } from "models/Person";
import { accountLocaleOptions, normalizeAccountLocale } from "pages/Account/translations";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Col, Row } from "reactstrap";
import { bindActionCreators } from "redux";
import * as api from "services/Api";
import { getPerson, updatePerson } from "store/actions/Customers";
import { PersonsState } from "store/reducers/Persons";
import { RootState } from "store/store";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    personState: PersonsState;
}

interface Actions {
    getPerson: (id: string) => Promise<Person>;
    updatePerson: (person: Person) => any;
}

interface RelationSelectorProps {
    accounts: Account[];
    saving: boolean;
    selectedId: string;
    onChange: (accountId: string) => void;
    onLink: () => void;
}

const clonePerson = (person: Person) => Object.assign(new Person(), {
    ...person,
    roles: [...(person.roles ?? [])],
    customers: [...(person.customers ?? [])],
    accounts: [...(person.accounts ?? [])],
    suppliers: [...(person.suppliers ?? [])],
});

const RelationSelector: React.FC<RelationSelectorProps> = ({
    accounts,
    saving,
    selectedId,
    onChange,
    onLink,
}) => {
    const { t } = useOfficeTranslation();
    const options = accounts.map(account => {
        const name = account.trade_names.length
            ? `${account.legal_name} (${account.trade_names.join(", ")})`
            : account.legal_name;
        const location = account.addresses.find(address => address.primary)?.city
            ?? account.addresses[0]?.city;
        const detail = account.email || location;

        return {
            value: account.id,
            label: detail ? `${name} — ${detail}` : name,
        };
    });

    return (
        <div>
            <label className="form-label" htmlFor="relation-account">{t("persons.personDetail.addAccountRelation")}</label>
            <div className="d-flex gap-2">
                <div className="flex-grow-1">
                    <Select
                        inputId="relation-account"
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        noOptionsMessage={() => t("persons.personDetail.noMatchingAccounts")}
                        options={options}
                        placeholder={t("common.searchAccounts")}
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 1060 }),
                        }}
                        value={options.find(option => option.value === selectedId) ?? null}
                        onChange={(option: any) => onChange(option?.value ?? "")}
                    />
                </div>
                <button
                    className="btn btn-outline-primary"
                    disabled={!selectedId || saving}
                    onClick={onLink}
                    type="button"
                >
                    <i className="mdi mdi-link-variant-plus me-1" />
                    {t("common.link")}
                </button>
            </div>
        </div>
    );
};

export const PersonDetail: React.FC<Props & Actions> = ({
    personState,
    getPerson,
    updatePerson,
}) => {
    const { t } = useOfficeTranslation();
    const { id } = GetRouterQuery<{ id: string }>();
    const person = personState.getPersonById(id);
    const [form, setForm] = useState<Person | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(!person);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingRelation, setSavingRelation] = useState(false);
    const [creatingAuthentication, setCreatingAuthentication] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState("");
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);

    useEffect(() => {
        setLoading(true);
        setLoadFailed(false);
        setLoadingAccounts(true);
        Promise.all([
            getPerson(id),
            api.getAccounts()
                .then(setAccounts)
                .finally(() => setLoadingAccounts(false)),
        ]).catch(() => setLoadFailed(true)).finally(() => setLoading(false));
    }, [getPerson, id]);

    useEffect(() => {
        if (person) {
            setForm(clonePerson(person));
        }
    }, [person]);

    const linkedAccounts = useMemo(
        () => accounts.filter(account =>
            (account.persons ?? []).some(link => link.person_id === id)
        ),
        [accounts, id],
    );
    const linkedAccountIds = useMemo(
        () => new Set(linkedAccounts.map(account => account.id)),
        [linkedAccounts],
    );
    const availableAccounts = useMemo(
        () => accounts.filter(account => !linkedAccountIds.has(account.id)),
        [accounts, linkedAccountIds],
    );

    const setField = (field: keyof Person, value: any) => {
        setForm(current => current
            ? Object.assign(new Person(), { ...current, [field]: value })
            : current
        );
    };

    const toggleRole = (role: string) => {
        setForm(current => current
            ? Object.assign(new Person(), {
                ...current,
                roles: current.roles.includes(role)
                    ? current.roles.filter(item => item !== role)
                    : [...current.roles, role],
            })
            : current
        );
    };

    const save = (event: React.FormEvent) => {
        event.preventDefault();
        if (!form?.name.trim()) {
            setMessage({ color: "danger", text: t("persons.personDetail.firstNameIsRequired") });
            return;
        }

        setSaving(true);
        setMessage(null);
        Promise.resolve(updatePerson(form))
            .then(() => setMessage({ color: "success", text: t("persons.personDetail.personSaved") }))
            .catch(() => setMessage({ color: "danger", text: t("persons.personDetail.couldNotSaveThisPerson") }))
            .finally(() => setSaving(false));
    };

    const createAuthentication = () => {
        if (!form?.email || form.auth !== null) {
            return;
        }

        setCreatingAuthentication(true);
        setMessage(null);
        api.createPersonAuthentication(form.id)
            .then(auth => {
                setForm(current => current
                    ? Object.assign(new Person(), { ...current, auth })
                    : current
                );
                setMessage({
                    color: "success",
                    text: t("persons.personDetail.authenticationCreatedThePasswordWasSentToEmail", {email: form.email}),
                });
            })
            .catch(reason => setMessage({
                color: "danger",
                text: String(reason.response?.data?.error ?? t("persons.personDetail.couldNotCreateAuthentication")),
            }))
            .finally(() => setCreatingAuthentication(false));
    };

    const linkAccount = (accountId: string) => {
        if (!form || !accountId) {
            return;
        }

        setSavingRelation(true);
        setMessage(null);
        api.linkAccountPerson(accountId, form.id)
            .then(updatedAccount => {
                setAccounts(current => current.map(account =>
                    account.id === updatedAccount.id ? updatedAccount : account
                ));
                setSelectedAccountId("");
                setMessage({ color: "success", text: t("persons.personDetail.accountRelationLinked") });
            })
            .catch(() => setMessage({ color: "danger", text: t("persons.personDetail.couldNotLinkThisAccount") }))
            .finally(() => setSavingRelation(false));
    };

    const unlinkAccount = (accountId: string) => {
        if (!form) {
            return;
        }

        setSavingRelation(true);
        setMessage(null);
        api.unlinkAccountPerson(accountId, form.id)
            .then(updatedAccount => {
                setAccounts(current => current.map(account =>
                    account.id === updatedAccount.id ? updatedAccount : account
                ));
                setMessage({ color: "success", text: t("persons.personDetail.accountRelationRemoved") });
            })
            .catch(() => setMessage({ color: "danger", text: t("persons.personDetail.couldNotRemoveThisRelation") }))
            .finally(() => setSavingRelation(false));
    };

    if (loading && !form) {
        return <div className="page-content text-center text-muted py-5">{t("persons.personDetail.loadingPerson")}</div>;
    }

    if (!form) {
        return (
            <div className="page-content">
                <Card>
                    <CardBody className="text-center py-5">
                        <h4>{t(loadFailed ? "persons.personDetail.couldNotLoadPerson" : "persons.personDetail.personNotFound")}</h4>
                        <Link to="/persons">{t("persons.personDetail.backToPersons")}</Link>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const fullName = `${form.name} ${form.last_name ?? ""}`.trim();
    const primaryAccountId = linkedAccounts[0]?.id;

    return (
        <div className="page-content">
            <PageMeta title={fullName} />

            <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <Link className="text-muted" to="/persons">
                        <i className="mdi mdi-arrow-left me-1" />
                        {t("common.persons")}
                    </Link>
                    <h4 className="mt-2 mb-1">{fullName}</h4>
                    <p className="text-muted mb-0">{form.title || t("persons.personDetail.personDetailsAndAccountRelations")}</p>
                </div>
            </div>

            {loadFailed && <Alert color="danger">{t("persons.personDetail.couldNotLoadPerson")}</Alert>}
            {message && <Alert color={message.color}>{message.text}</Alert>}

            <Row>
                <Col xl={4}>
                    <Card>
                        <CardBody>
                            <CardHeader title={t("common.profile")} icon="mdi-account-circle-outline" />

                            <div className="text-center border-bottom py-3">
                                <div className="avatar-lg mx-auto mb-3">
                                    <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-24">
                                        {`${form.name?.[0] ?? ""}${form.last_name?.[0] ?? ""}`.toUpperCase()}
                                    </span>
                                </div>
                                <h4 className="mb-1">{fullName}</h4>
                                <p className="text-muted">{form.title || t("persons.personDetail.noTitleSpecified")}</p>
                                <div className="d-flex justify-content-center gap-2">
                                    {form.email &&
                                        <Link
                                            className="btn btn-primary btn-sm"
                                            to={`/conversations/compose?person=${form.id}${primaryAccountId
                                                ? `&customer=${primaryAccountId}`
                                                : ""
                                            }`}
                                        >
                                            <i className="mdi mdi-email-outline me-1" />
                                            {t("common.email")}
                                        </Link>
                                    }
                                    {form.phone &&
                                        <a className="btn btn-light btn-sm" href={`tel:${form.phone}`}>
                                            <i className="mdi mdi-phone me-1" />
                                            {t("persons.personDetail.call")}
                                        </a>
                                    }
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-start gap-3 py-3">
                                <div>
                                    <span className="d-block text-muted mb-1">{t("persons.personDetail.authentication")}</span>
                                    {form.auth === null
                                        ? <span className="badge bg-light text-muted">{t("persons.personDetail.noAuthentication")}</span>
                                        : form.auth.mfa_active
                                            ? <span className="badge bg-success bg-soft text-success">{t("common.mfaActive")}</span>
                                            : <span className="badge bg-warning bg-soft text-warning">{t("common.mfaInactive")}</span>
                                    }
                                </div>
                                {form.auth === null && form.email &&
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        disabled={creatingAuthentication}
                                        onClick={createAuthentication}
                                        type="button"
                                    >
                                        <i className="mdi mdi-account-key-outline me-1" />
                                        {t(creatingAuthentication ? "common.actions.creating" : "common.actions.createLogin")}
                                    </button>
                                }
                            </div>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <CardHeader
                                title={t("persons.personDetail.linkedRelations")}
                                icon="mdi-link-variant"
                                count={linkedAccounts.length}
                            />

                            {loadingAccounts &&
                                <p className="text-muted text-center py-4 mb-0">{t("persons.personDetail.loadingRelations")}</p>
                            }

                            {!loadingAccounts && linkedAccounts.map(account =>
                                <div
                                    className="d-flex align-items-center justify-content-between gap-3 border-bottom py-3"
                                    key={account.id}
                                >
                                    <Link className="text-body text-decoration-none" to={`/accounts/${account.id}`}>
                                        <strong>{account.legal_name}</strong>
                                        <span className="d-flex flex-wrap gap-1 mt-1">
                                            {account.roles.map(role =>
                                                <span
                                                    className={`badge ${role === "customer"
                                                        ? "bg-primary bg-soft text-primary"
                                                        : "bg-warning bg-soft text-warning"
                                                    }`}
                                                    key={role}
                                                >
                                                    {t(role === "customer" ? "common.roles.customer" : "common.roles.supplier")}
                                                </span>
                                            )}
                                        </span>
                                    </Link>
                                    <div className="d-flex gap-1">
                                        <Link
                                            aria-label={t("persons.personDetail.viewAccount", {name: account.legal_name})}
                                            className="btn btn-sm btn-light"
                                            to={`/accounts/${account.id}`}
                                        >
                                            <i className="mdi mdi-chevron-right" />
                                        </Link>
                                        <button
                                            aria-label={t("persons.personDetail.unlinkAccount", {name: account.legal_name})}
                                            className="btn btn-sm btn-light text-danger"
                                            disabled={savingRelation}
                                            onClick={() => unlinkAccount(account.id)}
                                            type="button"
                                        >
                                            <i className="mdi mdi-link-variant-off" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loadingAccounts && linkedAccounts.length === 0 &&
                                <p className="text-muted mt-3 mb-0">{t("persons.personDetail.noAccountRelationsLinkedYet")}</p>
                            }

                            <div className="pt-3">
                                <RelationSelector
                                    accounts={availableAccounts}
                                    saving={savingRelation}
                                    selectedId={selectedAccountId}
                                    onChange={setSelectedAccountId}
                                    onLink={() => linkAccount(selectedAccountId)}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col xl={8}>
                    <Card>
                        <CardBody>
                            <CardHeader title={t("persons.personDetail.personDetails")} icon="mdi-account-edit-outline" />

                            <form className="pt-3" onSubmit={save}>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-first-name">{t("common.firstName")}</label>
                                            <input
                                                className="form-control"
                                                id="person-first-name"
                                                value={form.name ?? ""}
                                                onChange={event => setField("name", event.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-last-name">{t("common.lastName")}</label>
                                            <input
                                                className="form-control"
                                                id="person-last-name"
                                                value={form.last_name ?? ""}
                                                onChange={event => setField("last_name", event.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-title">{t("persons.personDetail.titleFunction")}</label>
                                            <input
                                                className="form-control"
                                                id="person-title"
                                                value={form.title ?? ""}
                                                onChange={event => setField("title", event.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-birth-date">{t("common.dateOfBirth")}</label>
                                            <input
                                                className="form-control"
                                                id="person-birth-date"
                                                type="date"
                                                max={new Date().toISOString().slice(0, 10)}
                                                value={form.birth_date ?? ""}
                                                onChange={event => setField("birth_date", event.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-locale">
                                                {t("persons.personDetail.customerPortalLanguage")}
                                            </label>
                                            <select
                                                className="form-select"
                                                id="person-locale"
                                                value={normalizeAccountLocale(form.locale)}
                                                onChange={event => setField("locale", event.target.value)}
                                            >
                                                {accountLocaleOptions.map(option =>
                                                    <option key={option.value} value={option.value}>
                                                        {option.nativeLabel}
                                                    </option>
                                                )}
                                            </select>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-email">{t("common.email")}</label>
                                            <input
                                                className="form-control"
                                                id="person-email"
                                                type="email"
                                                value={form.email ?? ""}
                                                onChange={event => setField("email", event.target.value)}
                                            />
                                            <div className="form-check form-switch mt-3">
                                                <input
                                                    checked={form.email_verified !== null}
                                                    className="form-check-input"
                                                    id="person-email-verified"
                                                    onChange={event => setField(
                                                        "email_verified",
                                                        event.target.checked ? new Date().toISOString() : null,
                                                    )}
                                                    type="checkbox"
                                                />
                                                <label className="form-check-label" htmlFor="person-email-verified">
                                                    {t("persons.personDetail.emailAddressVerified")}
                                                </label>
                                                <small className="d-block text-muted mt-1">
                                                    {t("persons.personDetail.emailAddressVerifiedHelp")}
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="person-phone">{t("common.phone")}</label>
                                            <input
                                                className="form-control"
                                                id="person-phone"
                                                value={form.phone ?? ""}
                                                onChange={event => setField("phone", event.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div className="mb-4">
                                            <label className="form-label d-block">{t("common.roles")}</label>
                                            {["billing", "technical"].map(role =>
                                                <div className="form-check form-check-inline" key={role}>
                                                    <input
                                                        checked={form.roles.includes(role)}
                                                        className="form-check-input"
                                                        id={`role-${role}`}
                                                        onChange={() => toggleRole(role)}
                                                        type="checkbox"
                                                    />
                                                    <label
                                                        className="form-check-label text-capitalize"
                                                        htmlFor={`role-${role}`}
                                                    >
                                                        {t(role === "billing" ? "common.billing" : "common.technical")}
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 border-top pt-3">
                                    <button
                                        className="btn btn-light"
                                        onClick={() => person && setForm(clonePerson(person))}
                                        type="button"
                                    >
                                        {t("persons.personDetail.reset")}
                                    </button>
                                    <button className="btn btn-primary" disabled={saving}>
                                        {t(saving ? "common.saving" : "common.actions.saveChanges")}
                                    </button>
                                </div>
                            </form>
                        </CardBody>
                    </Card>
                    {form.auth?.devices !== undefined && <DevicesAndSessions devices={form.auth.devices} />}
                </Col>
            </Row>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    personState: state.Persons,
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getPerson, updatePerson }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(PersonDetail);
